import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from '@/components/ui/sonner';
import { lazy, Suspense } from 'react';
import LoginPage from '@/pages/LoginPage';
import { AuthProvider, useAuth } from '@/context/AuthContext';
import { ThemeProvider, useTheme } from '@/context/ThemeContext';
import { WebSocketProvider } from '@/contexts/WebSocketContext';
import { PauseProvider } from '@/context/PauseContext';
import PauseBanner from '@/components/PauseBanner';
import '@/App.css';

// Lazy load heavy dashboard components for better initial load time
const AdminDashboard = lazy(() => import('@/pages/AdminDashboard'));
const ManagerDashboard = lazy(() => import('@/pages/ManagerDashboard'));
const WorkerDashboard = lazy(() => import('@/pages/WorkerDashboard'));
const ExternalDashboard = lazy(() => import('@/pages/ExternalDashboard'));
const SmartJobAnalytics = lazy(() => import('@/pages/SmartJobAnalytics'));
const HandbuchPage = lazy(() => import('@/pages/HandbuchPage'));

// Loading spinner component
const DashboardLoader = () => (
  <div className="min-h-screen bg-background flex items-center justify-center">
    <div className="text-center">
      <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
      <p className="text-muted-foreground">Dashboard wird geladen...</p>
    </div>
  </div>
);

const ProtectedRoute = ({ children, allowedRoles }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-muted-foreground">Laden...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (allowedRoles && !allowedRoles.includes(user.role)) {
    return <Navigate to="/" replace />;
  }

  return children;
};

const DashboardRouter = () => {
  const { user, token } = useAuth();

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Wrap lazy-loaded components in Suspense
  const renderDashboard = () => {
    if (user.role === 'admin') {
      return <AdminDashboard />;
    } else if (user.role === 'manager' || user.role === 'wiegemeister') {
      return <ManagerDashboard />;
    } else if (user.role === 'haendler' || user.role === 'instandhaltung') {
      return <ExternalDashboard />;
    } else {
      return <WorkerDashboard />;
    }
  };

  return (
    <WebSocketProvider token={token} userRole={user.role} userId={user.id}>
      <PauseProvider user={user}>
        <PauseBanner />
        <Suspense fallback={<DashboardLoader />}>
          {renderDashboard()}
        </Suspense>
      </PauseProvider>
    </WebSocketProvider>
  );
};

// Theme-aware Toaster component
const ThemedToaster = () => {
  const { theme } = useTheme();
  return <Toaster position="top-right" theme={theme} />;
};

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route
              path="/"
              element={
                <ProtectedRoute>
                  <DashboardRouter />
                </ProtectedRoute>
              }
            />
            <Route
              path="/analytics"
              element={
                <ProtectedRoute allowedRoles={['admin', 'manager', 'wiegemeister']}>
                  <Suspense fallback={<DashboardLoader />}>
                    <SmartJobAnalytics />
                  </Suspense>
                </ProtectedRoute>
              }
            />
            <Route
              path="/handbuch"
              element={
                <ProtectedRoute>
                  <Suspense fallback={<DashboardLoader />}>
                    <HandbuchPage />
                  </Suspense>
                </ProtectedRoute>
              }
            />
            <Route
              path="/manager/settings"
              element={
                <ProtectedRoute allowedRoles={['admin', 'manager']}>
                  <Suspense fallback={<DashboardLoader />}>
                    <AdminDashboard managerMode />
                  </Suspense>
                </ProtectedRoute>
              }
            />
          </Routes>
          <ThemedToaster />
        </BrowserRouter>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
